import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.phone.trim()) {
      // Phone is required and should be validated
      newErrors.phone = 'Phone number is required';
    } else if (!/^[+]?[0-9\s-]{6,20}$/.test(formData.phone)) {
      // Basic phone validation
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.email.trim()) {
      setError('email', 'Email address is required');
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.content && formData.content.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      if (result.status === 'ok') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 p-8 md:p-10 rounded-2xl border border-slate-100/80">
      <h3 className="text-2xl font-bold text-primary mb-6">Send us a message</h3>
      
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-emerald-500 mt-1" />
          <div>
            <p className="font-semibold">Thank you for your message!</p>
            <p className="text-sm text-emerald-700 mt-0.5">We have received your inquiry and will get back to you as soon as possible.</p>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-rose-500 mt-1" />
          <div>
            <p className="font-semibold">Submission failed</p>
            <p className="text-sm text-rose-700 mt-0.5">An error occurred while sending your message. Please try again or email us directly.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot field (absolutely hidden from humans) */}
        <div className="sr-only absolute" aria-hidden="true">
          <label htmlFor="website_url">Do not fill this field</label>
          <input
            id="website_url"
            type="text"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
            Name
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-slate-50/50 text-slate-900 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200 ${
              errors.name ? 'border-rose-300 focus:ring-rose-100 focus:border-rose-500' : 'border-slate-200'
            }`}
            placeholder="Your full name"
          />
          {errors.name && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.name}</p>}
        </div>

        {/* Phone Number Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
            Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-slate-50/50 text-slate-900 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200 ${
              errors.phone ? 'border-rose-300 focus:ring-rose-100 focus:border-rose-500' : 'border-slate-200'
            }`}
            placeholder="+45 00 00 00 00"
          />
          {errors.phone && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.phone}</p>}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
            E-mail Address
          </label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-slate-50/50 text-slate-900 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200 ${
              errors.email ? 'border-rose-300 focus:ring-rose-100 focus:border-rose-500' : 'border-slate-200'
            }`}
            placeholder="you@example.com"
          />
          {errors.email && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.email}</p>}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows="4"
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border bg-slate-50/50 text-slate-900 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200 resize-none ${
              errors.message ? 'border-rose-300 focus:ring-rose-100 focus:border-rose-500' : 'border-slate-200'
            }`}
            placeholder="How can we help you?"
          />
          {errors.message && <p className="mt-1.5 text-xs text-rose-600 font-medium">{errors.message}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-light transition-all-300 flex items-center justify-center gap-2 cursor-pointer shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner-third animate-spin text-lg" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <i className="fa-regular fa-paper-plane text-sm" />
              <span>Send Message</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
